import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import { categories } from "@/mocks/categories";
import {
  loadApplication,
  loadVerificationDocs,
  saveApplicationDraft,
  submitApplication,
  TERMS_VERSION,
  type SellerApplication,
  type VerificationDoc,
} from "@/lib/seller-application";
import LocationPicker, {
  type LocationValue,
} from "./components/LocationPicker";
import VerificationDocs from "./components/VerificationDocs";

const STEPS = [
  { label: "Personal", icon: "ri-user-line" },
  { label: "Negocio", icon: "ri-store-2-line" },
  { label: "Ubicación", icon: "ri-map-pin-line" },
  { label: "Identidad", icon: "ri-id-card-line" },
  { label: "Políticas", icon: "ri-file-check-line" },
  { label: "Revisar", icon: "ri-checkbox-circle-line" },
];

const SELLER_TYPES = [
  { value: "particular", label: "Particular", desc: "Vendo mis propios artículos." },
  { value: "empresa", label: "Empresa / Negocio", desc: "Tengo un comercio o tienda." },
];

const inputClass =
  "w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
const labelClass = "mb-1.5 block text-sm font-semibold text-foreground-800";

export default function SellerApplicationPage() {
  const { user, profile, refreshProfile } = useAuth();
  const { showToast, notify } = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  const [application, setApplication] = useState<SellerApplication | null>(null);
  const [docs, setDocs] = useState<VerificationDoc[]>([]);

  // Paso 1
  const [fullName, setFullName] = useState(profile?.full_name ?? "");
  const [phone, setPhone] = useState("");
  // Paso 2
  const [storeName, setStoreName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [sellerType, setSellerType] = useState("");
  const [primaryCategory, setPrimaryCategory] = useState("");
  const [secondaryCategories, setSecondaryCategories] = useState<string[]>([]);
  const [storeDescription, setStoreDescription] = useState("");
  // Paso 3
  const [location, setLocation] = useState<LocationValue>({
    country: "",
    province: "",
    city: "",
    address: "",
    latitude: null,
    longitude: null,
  });
  // Paso 5
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (!user) return;
    let mounted = true;

    async function load() {
      try {
        const app = await loadApplication(user.id);
        const loadedDocs = await loadVerificationDocs(user.id);
        if (!mounted) return;
        setDocs(loadedDocs);
        if (app) {
          setApplication(app);
          setStep(app.current_step ?? 1);
          setPhone(app.seller_phone ?? "");
          setStoreName(app.store_name ?? "");
          setBusinessName(app.business_name ?? "");
          setSellerType(app.seller_type ?? "");
          setPrimaryCategory(app.primary_category ?? "");
          setSecondaryCategories(app.secondary_categories ?? []);
          setStoreDescription(app.store_description ?? "");
          setLocation({
            country: app.country ?? "",
            province: app.province ?? "",
            city: app.city ?? "",
            address: app.address ?? "",
            latitude: app.latitude,
            longitude: app.longitude,
          });
          setAcceptedTerms(app.accepted_terms ?? false);
        }
      } catch {
        // Silencioso: se puede reintentar al guardar.
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  const isPendingOrFurther =
    profile?.seller_status === "pending" ||
    profile?.seller_status === "under_review" ||
    profile?.seller_status === "approved" ||
    profile?.seller_status === "suspended";

  async function persistDraft(nextStep: number): Promise<SellerApplication> {
    if (!user) throw new Error("No estás autenticado.");
    const trimmedName = fullName.trim();
    if (trimmedName && trimmedName !== (profile?.full_name ?? "")) {
      await supabase
        .from("profiles")
        .update({ full_name: trimmedName })
        .eq("id", user.id);
    }
    const app = await saveApplicationDraft(user.id, {
      seller_phone: phone.trim() || null,
      store_name: storeName.trim() || null,
      business_name: businessName.trim() || null,
      seller_type: sellerType || null,
      primary_category: primaryCategory || null,
      secondary_categories: secondaryCategories.length
        ? secondaryCategories
        : null,
      store_description: storeDescription.trim() || null,
      country: location.country.trim() || null,
      province: location.province.trim() || null,
      city: location.city.trim() || null,
      address: location.address.trim() || null,
      latitude: location.latitude,
      longitude: location.longitude,
      accepted_terms: acceptedTerms,
      terms_version: acceptedTerms ? TERMS_VERSION : null,
      terms_accepted_at: acceptedTerms ? new Date().toISOString() : null,
      current_step: nextStep,
    });
    setApplication(app);
    return app;
  }

  function validateStep(s: number): string | null {
    if (s === 1) {
      if (fullName.trim().length < 2) return "Completá tu nombre completo.";
      if (!phone.trim()) return "Ingresá tu teléfono de contacto.";
    }
    if (s === 2) {
      if (!storeName.trim()) return "Ingresá el nombre de tu tienda.";
      if (!sellerType) return "Seleccioná el tipo de vendedor.";
      if (!primaryCategory) return "Seleccioná tu categoría principal.";
    }
    if (s === 3) {
      if (!location.city.trim() || !location.country.trim())
        return "Completá tu ciudad y país.";
      if (location.latitude == null || location.longitude == null)
        return "Confirmá tu ubicación en el mapa.";
    }
    if (s === 4) {
      const hasFront = docs.some((d) => d.document_type === "front");
      const hasBack = docs.some((d) => d.document_type === "back");
      if (!hasFront || !hasBack)
        return "Subí tu cédula frontal y posterior.";
    }
    if (s === 5) {
      if (!acceptedTerms)
        return "Debés aceptar las políticas de vendedores.";
    }
    return null;
  }

  async function handleNext() {
    const err = validateStep(step);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError(null);
    setSaving(true);
    try {
      const next = step + 1;
      await persistDraft(next);
      setStep(next);
    } catch (e) {
      showToast(
        e instanceof Error ? e.message : "No se pudo guardar el avance.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }

  function handleBack() {
    setStepError(null);
    setStep((s) => Math.max(1, s - 1));
  }

  async function handleSubmit() {
    setSaving(true);
    setStepError(null);
    try {
      const app = await persistDraft(6);
      await submitApplication(app.id);
      await refreshProfile();
      setSubmitted(true);
      notify({
        type: "celebration",
        title: t("notify.seller.submitted.title"),
        message: t("notify.seller.submitted.message"),
      });
    } catch (e) {
      const raw = e instanceof Error ? e.message : "";
      setStepError(
        /failed to fetch/i.test(raw)
          ? "No pudimos conectar con el servidor. Revisá tu conexión a internet y volvé a intentarlo."
          : raw || "No se pudo enviar la solicitud."
      );
    } finally {
      setSaving(false);
    }
  }

  const primaryLabel = useMemo(
    () => categories.find((c) => c.name === primaryCategory)?.name ?? "",
    [primaryCategory]
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
      </div>
    );
  }

  // Estado: ya enviada / en revisión / aprobada / suspendida
  if (submitted || isPendingOrFurther) {
    const status = submitted ? "submitted" : profile?.seller_status;
    const isApproved = status === "approved";
    const isSuspended = status === "suspended";
    return (
      <div className="mx-auto flex min-h-[70vh] w-full max-w-xl flex-col items-center justify-center px-4 py-16 text-center">
        <span
          className={`flex h-16 w-16 items-center justify-center rounded-full ${
            isApproved
              ? "bg-primary-100 text-primary-600"
              : isSuspended
                ? "bg-accent-100 text-accent-700"
                : "bg-secondary-100 text-secondary-700"
          }`}
        >
          <i
            className={`${
              isApproved
                ? "ri-store-2-fill"
                : isSuspended
                  ? "ri-pause-circle-fill"
                  : "ri-time-fill"
            } text-3xl`}
          ></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          {isApproved
            ? "Ya sos vendedor"
            : isSuspended
              ? "Cuenta suspendida"
              : "Solicitud en revisión"}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          {isApproved
            ? "Tu cuenta de vendedor está activa. Ya podés publicar productos."
            : isSuspended
              ? "Tu cuenta de vendedor fue suspendida. Contactá a soporte."
              : "Recibimos tu solicitud. Un administrador la revisará y te avisaremos por correo."}
        </p>
        <div className="mt-6 flex items-center gap-3">
          {isApproved && (
            <Link
              to="/seller/dashboard"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-dashboard-line"></i>
              Ir a mi panel
            </Link>
          )}
          <Link
            to="/perfil"
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            Volver a mi perfil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <div className="mx-auto w-full max-w-3xl px-4 py-6 md:px-6">
        {/* Encabezado */}
        <div className="mb-6 flex items-center gap-3">
          <Link
            to="/perfil"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
          >
            <i className="ri-arrow-left-line"></i>
          </Link>
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">
              Registrarme como vendedor
            </h1>
            <p className="text-sm text-foreground-500">
              Publica tus artículos en VendeYa.
            </p>
          </div>
        </div>

        {/* Barra de progreso */}
        <div className="mb-8">
          <div className="mb-2 flex items-center justify-between text-xs font-medium text-foreground-500">
            <span>
              Paso {step} de {STEPS.length}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-save-line"></i>
              Tu avance se guarda solo
            </span>
          </div>
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-background-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: `${(step / STEPS.length) * 100}%` }}
            ></div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {STEPS.map((s, i) => {
              const n = i + 1;
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => n < step && setStep(n)}
                  className={`flex cursor-pointer flex-col items-center gap-1 whitespace-nowrap text-center ${
                    n < step
                      ? "text-primary-600"
                      : n === step
                        ? "text-foreground-900"
                        : "text-foreground-400"
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                      n < step
                        ? "bg-primary-500 text-white"
                        : n === step
                          ? "bg-primary-100 text-primary-700"
                          : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {n < step ? <i className="ri-check-line"></i> : n}
                  </span>
                  <span className="hidden text-[10px] sm:inline">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Contenido del paso */}
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  Información personal
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  Tu nombre y cómo te contactamos.
                </p>
              </div>
              <div>
                <label className={labelClass} htmlFor="sa-fullname">
                  Nombre completo
                </label>
                <input
                  id="sa-fullname"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="sa-phone">
                  Teléfono de contacto
                </label>
                <input
                  id="sa-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ej. +1 809 555 0123"
                  className={inputClass}
                />
                <p className="mt-1.5 text-xs text-foreground-400">
                  No se mostrará públicamente salvo que lo actives en Privacidad.
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  Información del negocio
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  Contanos qué vendés y cómo se llama tu tienda.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="sa-store">
                    Nombre de la tienda
                  </label>
                  <input
                    id="sa-store"
                    type="text"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    placeholder="Ej. Tecnología Punta Cana"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="sa-business">
                    Nombre comercial (opcional)
                  </label>
                  <input
                    id="sa-business"
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Razón social"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Tipo de vendedor</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {SELLER_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setSellerType(t.value)}
                      className={`cursor-pointer whitespace-nowrap rounded-md border px-4 py-3 text-left transition-colors ${
                        sellerType === t.value
                          ? "border-primary-400 bg-primary-100 text-primary-800"
                          : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <span className="block text-sm font-semibold">
                        {t.label}
                      </span>
                      <span className="block text-xs text-foreground-500">
                        {t.desc}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelClass} htmlFor="sa-primary">
                  Categoría principal
                </label>
                <select
                  id="sa-primary"
                  value={primaryCategory}
                  onChange={(e) => setPrimaryCategory(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Selecciona una categoría</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Categorías secundarias (opcional)</label>
                <div className="flex flex-wrap gap-2">
                  {categories
                    .filter((c) => c.name !== primaryCategory)
                    .map((c) => {
                      const active = secondaryCategories.includes(c.name);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() =>
                            setSecondaryCategories((prev) =>
                              active
                                ? prev.filter((x) => x !== c.name)
                                : [...prev, c.name]
                            )
                          }
                          className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                            active
                              ? "border-primary-400 bg-primary-100 text-primary-800"
                              : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                          }`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                </div>
              </div>

              <div>
                <label className={labelClass} htmlFor="sa-desc">
                  Descripción del negocio
                </label>
                <textarea
                  id="sa-desc"
                  value={storeDescription}
                  maxLength={500}
                  onChange={(e) => setStoreDescription(e.target.value)}
                  placeholder="Contá brevemente qué vendés y a quién."
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
                <p className="mt-1.5 text-right text-xs text-foreground-400">
                  {storeDescription.length}/500
                </p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  Ubicación
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  Buscá tu dirección y confirmá el punto en el mapa. La
                  ubicación exacta es privada.
                </p>
              </div>
              <LocationPicker
                value={location}
                onChange={(patch) =>
                  setLocation((prev) => ({ ...prev, ...patch }))
                }
              />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  Verificación de identidad
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  Subí una foto de tu cédula de identidad (frente y reverso).
                </p>
              </div>
              <VerificationDocs
                userId={user?.id ?? ""}
                applicationId={application?.id ?? null}
                docs={docs}
                onDocsChange={setDocs}
              />
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  Políticas de vendedores
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  Leé atentamente antes de aceptar.
                </p>
              </div>
              <div className="max-h-64 space-y-3 overflow-y-auto rounded-md border border-background-200 bg-background-100/40 p-4 text-sm text-foreground-700">
                <p>
                  <strong>1. Publicaciones con ticket.</strong> Todo artículo
                  publicado debe estar respaldado por un ticket único generado
                  por VendeYa (formato VY-AAAA-######). Las imágenes de la
                  galería deben contener ese ticket.
                </p>
                <p>
                  <strong>2. Contenido veraz.</strong> Las publicaciones deben
                  describir con precisión el producto. No se permite publicar
                  artículos falsos, robados o prohibidos.
                </p>
                <p>
                  <strong>3. Verificación.</strong> Para vender debés completar
                  la verificación de identidad. Tus documentos son privados.
                </p>
                <p>
                  <strong>4. Conducta.</strong> El trato respetuoso con
                  compradores y otros vendedores es obligatorio.
                </p>
                <p>
                  <strong>5. Cumplimiento.</strong> VendeYa puede moderar,
                  suspender o rechazar publicaciones y cuentas que incumplan
                  estas políticas.
                </p>
              </div>
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 cursor-pointer rounded border-background-300 accent-primary-500"
                />
                <span className="text-sm text-foreground-800">
                  He leído y acepto las políticas de vendedores de VendeYa
                  (versión {TERMS_VERSION}).
                </span>
              </label>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  Revisá tu solicitud
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  Confirmá que todo esté correcto antes de enviar.
                </p>
              </div>

              <dl className="space-y-3 rounded-md border border-background-200 bg-background-100/40 p-4 text-sm">
                <div>
                  <dt className="text-foreground-400">Nombre</dt>
                  <dd className="font-semibold text-foreground-900">
                    {fullName || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-foreground-400">Teléfono</dt>
                  <dd className="font-semibold text-foreground-900">
                    {phone || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-foreground-400">Tienda</dt>
                  <dd className="font-semibold text-foreground-900">
                    {storeName || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-foreground-400">Tipo de vendedor</dt>
                  <dd className="font-semibold text-foreground-900">
                    {SELLER_TYPES.find((t) => t.value === sellerType)?.label ??
                      "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-foreground-400">Categoría</dt>
                  <dd className="font-semibold text-foreground-900">
                    {primaryLabel || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-foreground-400">Ubicación</dt>
                  <dd className="font-semibold text-foreground-900">
                    {[location.city, location.province, location.country]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-foreground-400">Documentos</dt>
                  <dd className="font-semibold text-foreground-900">
                    {docs.some((d) => d.document_type === "front")
                      ? "Cédula frontal ✓"
                      : "Cédula frontal ✗"}{" "}
                    ·{" "}
                    {docs.some((d) => d.document_type === "back")
                      ? "Cédula posterior ✓"
                      : "Cédula posterior ✗"}
                  </dd>
                </div>
                <div>
                  <dt className="text-foreground-400">Políticas</dt>
                  <dd className="font-semibold text-foreground-900">
                    {acceptedTerms ? "Aceptadas ✓" : "No aceptadas"}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {stepError && (
            <p className="mt-4 flex items-center gap-1.5 text-sm font-medium text-accent-700">
              <i className="ri-error-warning-line"></i>
              {stepError}
            </p>
          )}
        </div>

        {/* Botones */}
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 1}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-line"></i>
            Atrás
          </button>

          {step < 6 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={saving}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-arrow-right-line"></i>
              )}
              Continuar
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enviando…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  Enviar solicitud
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}